import { liveQuery } from 'dexie';
import { constantLive, ProblemError, type ComposerDraft, type ComposerDraftState, type DraftService, type Id } from '@/model';
import { object } from './decode';
import { applyRemoteDraft, type DraftRow } from './store';
import { requireStore, type ResourceDependencies } from './resources';

export function visibleDraft(row?: DraftRow): ComposerDraft | undefined {
  if (!row || !row.text && !row.replyToId && !row.editingMessageId && !row.attachments?.length && !row.suspended) return undefined;
  const { conversation_id: _id, remote_revision: _remote, local_revision: _revision, remote_draft: _pending, local: _dirty, source: _source, ...draft } = row;
  return draft;
}
function wireDraft(row: DraftRow) {
  const source = row.editingMessageId ? row.suspended : row;
  return { text: source?.text ?? '', reply_to_id: source?.replyToId ?? null };
}

/** One account-bound writer. Blob, edit and suspended state never leave IndexedDB. */
export function createDrafts(deps: Pick<ResourceDependencies, 'http' | 'queries' | 'store'>): DraftService & { start(): void; stop(): void; setPaused(value: boolean): void } {
  const absent = constantLive(undefined);
  const absentState = constantLive<ComposerDraftState>({ draft: undefined, restoring: false, revision: 0, hasUnsyncedEdits: false, source: 'local' });
  let running = false; let paused = false; let generation = 0;
  let subscription: { unsubscribe(): void } | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let flight: Promise<void> | undefined;
  const schedule = (delay = 500) => {
    if (!running || paused || timer || flight) return;
    timer = setTimeout(() => { timer = undefined; void pump(); }, delay);
  };
  const pump = async () => {
    const db = deps.store(); if (!db || !running || paused || flight) return;
    const current = generation;
    const run = async () => {
      const rows = await db.drafts.filter(row => row.local === true).toArray();
      for (const row of rows) {
        if (db !== deps.store() || current !== generation || !running || paused) return;
        const fence = (await db.meta.get('sync'))?.generation;
        const response = object(await deps.http.put(`/v1/conversations/${row.conversation_id}/draft`, wireDraft(row), { cause: 'background' }));
        if (db !== deps.store() || current !== generation || !running) return;
        await db.transaction('rw', db.drafts, db.meta, async () => {
          if ((await db.meta.get('sync'))?.generation !== fence) return;
          const held = await db.drafts.get(row.conversation_id);
          if (!held) return;
          const remote = response.draft && typeof response.draft === 'object' ? object(response.draft) : undefined;
          if (held.local_revision === row.local_revision && held.updatedAt === row.updatedAt) {
            await db.drafts.put({ ...held, remote_draft: undefined, local: false, remote_revision: Math.max(held.remote_revision ?? 0, Number(remote?.revision ?? 0)) });
            if (held.remote_draft && Number(held.remote_draft.revision ?? 0) > Number(remote?.revision ?? 0)) await applyRemoteDraft(db, held.remote_draft);
          } else if (remote) await applyRemoteDraft(db, remote);
        });
      }
    };
    flight = (globalThis.navigator?.locks ? navigator.locks.request(`hylo.web.drafts:${db.accountId}`, { ifAvailable: true }, async lock => { if (lock) await run(); }) : run()).catch(error => {
      // Keep local work on rejection. Rate limiting and offline failures are retried, never discarded.
      const delay = error instanceof ProblemError && error.retryAfter ? error.retryAfter * 1000 : 30000;
      if (current === generation) { flight = undefined; schedule(delay); }
    }).finally(() => {
      if (current !== generation) return;
      flight = undefined;
      void db.drafts.filter(row => row.local === true).count().then(count => { if (count && current === generation) schedule(); }).catch(() => undefined);
    });
    await flight;
  };
  const save = async (id: Id, draft: ComposerDraft) => {
    const captured = structuredClone(draft); const db = requireStore(deps);
    await db.transaction('rw', db.drafts, db.erasedConversations, async () => {
      if (db !== deps.store() || await db.erasedConversations.get(id)) return;
      const old = await db.drafts.get(id);
      await db.drafts.put({ ...old, ...captured, replyToId: captured.replyToId, editingMessageId: captured.editingMessageId, suspended: captured.suspended, attachments: captured.attachments, conversation_id: id, updatedAt: Math.max(captured.updatedAt, (old?.updatedAt ?? -1) + 1), local_revision: (old?.local_revision ?? 0) + 1, source: 'local', local: true });
    });
    schedule();
  };
  return {
    draft: id => !id.trim() ? absent : deps.queries.get(`draft:${id}`, undefined, async db => visibleDraft(await db.drafts.get(id))),
    state: id => !id.trim() ? absentState : deps.queries.get<ComposerDraftState>(`draft-state:${id}`, { draft: undefined, restoring: true, revision: 0, hasUnsyncedEdits: false, source: 'local' }, async db => {
      const row = await db.drafts.get(id);
      return { draft: visibleDraft(row), restoring: false, revision: row?.local_revision ?? row?.remote_revision ?? 0, hasUnsyncedEdits: row?.local === true, source: row?.source ?? (row?.local ? 'local' : 'remote') };
    }),
    save, clear: id => save(id, { text: '', updatedAt: Date.now() }),
    start() {
      running = true; generation++;
      const db = deps.store(); if (!db) return;
      subscription?.unsubscribe();
      subscription = liveQuery(() => db.drafts.filter(row => row.local === true).count()).subscribe({ next: count => { if (count) schedule(); }, error: () => undefined });
      schedule();
    },
    setPaused(value) { paused = value; if (!paused) schedule(); },
    stop() { running = false; generation++; subscription?.unsubscribe(); subscription = undefined; if (timer) clearTimeout(timer); timer = undefined; flight = undefined; },
  };
}
